import React from "react";
import Badge from "shared/Badge/Badge";
import ButtonSecondary from "shared/Button/ButtonSecondary";
import Avatar from "shared/Avatar/Avatar";
import { useSelector } from "react-redux";
import { RootState } from "redux/store";
import BtnLikeIcon from "components/BtnLikeIcon/BtnLikeIcon";
import { PropertyToEdit } from "redux/reducers/Properties/PropertiesSliceTypes";
import { Users, Bed, Bath, Maximize2, Edit3, Eye, Shield, Lock, CreditCard, CheckCircle, Calendar } from "lucide-react";
import { useState } from "react";
import { Modal } from "@mantine/core";
import ShareButton from "components/ShareButton/ShareButton";

interface RenderSectionDetailsProps {
  propertyData: PropertyToEdit | null;
  slug: string;
  setPropertyData: any;
}

const PropertyHostOverview: React.FC<RenderSectionDetailsProps> = ({
  propertyData,
  slug,
  setPropertyData,
}) => {
  const { currentUser } = useSelector((state: RootState) => state.user);
  const [modalType, setModalType] = useState<'website' | 'guarantee' | 'security' | null>(null);
  const [viewersCount] = useState(Math.floor(Math.random() * 5) + 2); // Random 2-6 viewers
  
  if (!propertyData) return null;

  const propertyStats = [
    {
      icon: <Users size={20} />,
      value: propertyData.guest_number,
      label: propertyData.guest_number === 1 ? "guest" : "guests",
    },
    {
      icon: <Bed size={20} />,
      value: propertyData.total_bedrooms,
      label: propertyData.total_bedrooms === 1 ? "bedroom" : "bedrooms",
    },
    {
      icon: <Bath size={20} />,
      value: propertyData.total_bathrooms,
      label: propertyData.total_bathrooms === 1 ? "bath" : "baths",
    },
    {
      icon: <Maximize2 size={20} />,
      value: propertyData.property_size,
      label: "square (m²)",
    },
  ];

  return (
    <div className="w-full bg-white dark:bg-neutral-900 rounded-2xl border border-neutral-200 dark:border-neutral-800 overflow-hidden">
      {/* Header Section */}
      <div className="px-6 py-5 border-b border-neutral-200 dark:border-neutral-800">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Badge name={propertyData.property_type?.name || "Property"} />
          </div>
          
          <div className="flex items-center gap-2">
            <ShareButton
              propertyId={propertyData.id}
              slug={slug}
              title={propertyData.title}
              city={propertyData.city}
              variant="full"
              surface="property_detail"
            />
            {currentUser?.is_student && (
              <BtnLikeIcon
                isLiked={propertyData?.is_favorite}
                propertyId={propertyData?.id}
                path="propertyDetail"
                propertyData={propertyData}
                setPropertyData={setPropertyData}
              />
            )}

            {propertyData?.user?.is_host && currentUser?.id === propertyData?.user?.id && (
              <ButtonSecondary href={`/add-listing-1/${slug}`}>
                <div className="flex items-center gap-2">
                  <Edit3 size={16} />
                  <span>Edit</span>
                </div>
              </ButtonSecondary>
            )}
          </div>
        </div>
      </div>

      {/* Property Stats Grid */}
      <div className="px-6 py-5">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {propertyStats.map((stat, index) => (
            <div
              key={index}
              className="flex flex-col items-start gap-2 p-3 rounded-xl hover:bg-gray-50 dark:hover:bg-neutral-800/50 transition-colors"
            >
              <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                {stat.icon}
              </div>
              <div className="flex flex-col">
                <span className="text-lg font-semibold text-gray-900 dark:text-white">
                  {stat.value}
                </span>
                <span className="text-xs text-gray-500 dark:text-gray-400 capitalize">
                  {stat.label}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Minimum Booking Duration Section */}
      {propertyData.minimum_months_of_booking && (
        <div className="px-6 py-4 bg-blue-50 dark:bg-blue-900/20 border-t border-neutral-200 dark:border-neutral-800">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/40 flex-shrink-0">
              <Calendar size={20} className="text-blue-600 dark:text-blue-400" />
            </div>
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-100">
                Minimum Stay Required
              </h4>
              <p className="text-sm text-blue-700 dark:text-blue-300 mt-0.5">
                This property requires a minimum booking of{" "}
                <span className="font-bold">
                  {propertyData.minimum_months_of_booking}{" "}
                  {propertyData.minimum_months_of_booking === 1 ? "month" : "months"}
                </span>
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Trust & Security Section */}
      <div className="px-6 py-5 bg-gradient-to-br from-orange-50 to-orange-100/50 dark:from-orange-900/10 dark:to-orange-800/5 border-t border-neutral-200 dark:border-neutral-800">
        {/* Current Viewers */}
        <div className="flex items-center gap-2 mb-4 pb-4 border-b border-orange-200/50 dark:border-orange-800/30">
          <Eye size={18} className="text-orange-600 dark:text-orange-400" />
          <span className="text-sm text-gray-700 dark:text-gray-300">
            <span className="font-semibold text-orange-600 dark:text-orange-400">{viewersCount}</span> users viewing this property now
          </span>
        </div>

        {/* Trust Badges */}
        <div className="space-y-3">
          <button
            onClick={() => setModalType('website')}
            className="w-full flex items-start gap-3 p-3 rounded-xl hover:bg-white dark:hover:bg-neutral-800 transition-all group"
          >
            <div className="p-2 rounded-lg bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 group-hover:scale-110 transition-transform">
              <CheckCircle size={20} />
            </div>
            <div className="flex-1 text-left">
              <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
                Official Website
              </h4>
              <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
                Best prices & instant processing
              </p>
            </div>
          </button>

          <button
            onClick={() => setModalType('guarantee')}
            className="w-full flex items-start gap-3 p-3 rounded-xl hover:bg-white dark:hover:bg-neutral-800 transition-all group"
          >
            <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 group-hover:scale-110 transition-transform">
              <Shield size={20} />
            </div>
            <div className="flex-1 text-left">
              <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
                NR Guaranteed
              </h4>
              <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
                24-hour protection guarantee
              </p>
            </div>
          </button>

          <button
            onClick={() => setModalType('security')}
            className="w-full flex items-start gap-3 p-3 rounded-xl hover:bg-white dark:hover:bg-neutral-800 transition-all group"
          >
            <div className="p-2 rounded-lg bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 group-hover:scale-110 transition-transform">
              <Lock size={20} />
            </div>
            <div className="flex-1 text-left">
              <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
                Secured Transaction
              </h4>
              <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
                SSL encrypted & biometric security
              </p>
            </div>
          </button>
        </div>
      </div>

      {/* Modals */}
      <InfoModal
        isOpen={modalType === 'website'}
        onClose={() => setModalType(null)}
        title="Official Website"
        icon={<CheckCircle size={32} className="text-orange-500" />}
        iconBg="bg-orange-100 dark:bg-orange-900/30"
      >
        <div className="space-y-3">
          <p className="text-sm text-gray-700 dark:text-gray-300">
            You are guaranteed to benefit from:
          </p>
          <ul className="space-y-2 ml-4">
            <li className="flex items-start gap-2 text-sm text-gray-600 dark:text-gray-400">
              <span className="text-orange-500 mt-1">•</span>
              <span>The best available prices</span>
            </li>
            <li className="flex items-start gap-2 text-sm text-gray-600 dark:text-gray-400">
              <span className="text-orange-500 mt-1">•</span>
              <span>Instant processing of your booking</span>
            </li>
            <li className="flex items-start gap-2 text-sm text-gray-600 dark:text-gray-400">
              <span className="text-orange-500 mt-1">•</span>
              <span>A reservation without intermediaries</span>
            </li>
            <li className="flex items-start gap-2 text-sm text-gray-600 dark:text-gray-400">
              <span className="text-orange-500 mt-1">•</span>
              <span>A secure transaction</span>
            </li>
          </ul>
        </div>
      </InfoModal>

      <InfoModal
        isOpen={modalType === 'guarantee'}
        onClose={() => setModalType(null)}
        title="Nomadroof Guarantee"
        icon={<Shield size={32} className="text-blue-500" />}
        iconBg="bg-blue-100 dark:bg-blue-900/30"
      >
        <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
          We guarantee the safety of your booking by transferring the first month's rent to the host only 24 hours after the check-in date you have selected, so you can confirm that everything is as advertised.
        </p>
      </InfoModal>

      <InfoModal
        isOpen={modalType === 'security'}
        onClose={() => setModalType(null)}
        title="Secured Transaction"
        icon={<Lock size={32} className="text-green-500" />}
        iconBg="bg-green-100 dark:bg-green-900/30"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
            Our website features advanced online security mechanisms. Each stage of the booking process is protected using Secure Socket Layer (SSL) technology, the leading technology in this field.
          </p>
          <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
            Your information is encrypted and protected using server authentication and data encryption mechanisms. The booking system is also hosted in a secure server environment with biometric security mechanisms, a firewall and other advanced technologies to prevent interference and intrusion.
          </p>
          
          {/* Payment Methods */}
          <div className="pt-4 border-t border-neutral-200 dark:border-neutral-700">
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">Accepted payment methods:</p>
            <div className="flex items-center gap-3 flex-wrap">
              <div className="px-3 py-2 bg-white dark:bg-neutral-800 border border-neutral-300 dark:border-neutral-700 rounded-lg">
                <CreditCard size={24} className="text-gray-600 dark:text-gray-400" />
              </div>
              <div className="px-3 py-2 bg-white dark:bg-neutral-800 border border-neutral-300 dark:border-neutral-700 rounded-lg">
                <span className="text-sm font-semibold text-blue-600">VISA</span>
              </div>
              <div className="px-3 py-2 bg-white dark:bg-neutral-800 border border-neutral-300 dark:border-neutral-700 rounded-lg">
                <span className="text-sm font-semibold text-orange-600">MC</span>
              </div>
              <div className="px-3 py-2 bg-white dark:bg-neutral-800 border border-neutral-300 dark:border-neutral-700 rounded-lg">
                <span className="text-sm font-semibold text-blue-700">AMEX</span>
              </div>
            </div>
          </div>
        </div>
      </InfoModal>
    </div>
  );
};

// Reusable Info Modal Component
const InfoModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  title: string;
  icon: React.ReactNode;
  iconBg: string;
  children: React.ReactNode;
}> = ({ isOpen, onClose, title, icon, iconBg, children }) => (
  <Modal
    opened={isOpen}
    onClose={onClose}
    size="md"
    centered
    withCloseButton={false}
    radius="xl"
    overlayProps={{
      backgroundOpacity: 0.55,
      blur: 3,
    }}
  >
    <div className="p-6">
      <div className="flex items-start gap-4 mb-6">
        <div className={`p-3 rounded-xl ${iconBg}`}>
          {icon}
        </div>
        <div className="flex-1">
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
            {title}
          </h3>
        </div>
      </div>
      {children}
    </div>
  </Modal>
);

export default PropertyHostOverview;